import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InstitucionFormulario } from '../../../../../core/models/postulaciones.models';
import { FormularioEditable } from '../postulacion-formulario.models';

@Component({
  selector: 'app-postulacion-paso-instituciones',
  imports: [CommonModule, FormsModule],
  templateUrl: './postulacion-paso-instituciones.html',
  styleUrl: './postulacion-formulario-paso.shared.css',
})
export class PostulacionPasoInstituciones implements OnChanges {
  @Input({ required: true }) formulario!: FormularioEditable;
  @Input() soloLectura = false;
  @Output() changed = new EventEmitter<void>();

  institucionNueva: InstitucionFormulario = this.crearInstitucionVacia();

  ngOnChanges(): void {
    this.asegurarInstituciones();
  }

  registrarCambio(): void {
    this.sincronizarInstitucionPrincipal();
    this.changed.emit();
  }

  get puedeAgregarInstitucion(): boolean {
    return (
      !this.soloLectura &&
      [this.institucionNueva.nombreInstitucion, this.institucionNueva.unidadInstitucional, this.institucionNueva.departamentoFacultad].every(
        (valor) => this.esValorInstitucionReal(valor)
      )
    );
  }

  agregarInstitucion(): void {
    if (!this.puedeAgregarInstitucion) return;

    const rol = this.formulario.instituciones.length === 0 ? 'principal' : this.institucionNueva.rol;
    const nuevaInstitucion: InstitucionFormulario = {
      nombreInstitucion: this.institucionNueva.nombreInstitucion.trim(),
      unidadInstitucional: this.institucionNueva.unidadInstitucional.trim(),
      departamentoFacultad: this.institucionNueva.departamentoFacultad.trim(),
      rol,
    };

    if (rol === 'principal') {
      this.formulario.instituciones = [
        nuevaInstitucion,
        ...this.formulario.instituciones.map((institucion) => ({
          ...institucion,
          rol: 'asociada' as const,
        })),
      ];
    } else {
      this.formulario.instituciones = [...this.formulario.instituciones, nuevaInstitucion];
    }

    this.institucionNueva = this.crearInstitucionVacia(this.formulario.instituciones.length === 0 ? 'principal' : 'asociada');
    this.registrarCambio();
  }

  eliminarInstitucion(index: number): void {
    const institucion = this.formulario.instituciones[index];
    if (this.soloLectura || !institucion || institucion.rol === 'principal' || this.formulario.instituciones.length <= 1) return;

    this.formulario.instituciones = this.formulario.instituciones.filter((_, itemIndex) => itemIndex !== index);
    this.registrarCambio();
  }

  etiquetaInstitucion(institucion: InstitucionFormulario, index: number): string {
    return institucion.rol === 'principal' ? 'Institucion principal' : `Institucion secundaria ${index}`;
  }

  etiquetaTipoInstitucion(rol: InstitucionFormulario['rol']): string {
    return rol === 'principal' ? 'Principal' : 'Secundaria';
  }

  claseTipoInstitucion(rol: InstitucionFormulario['rol']): string {
    return rol === 'principal' ? 'status-ok' : 'status-pending';
  }

  private asegurarInstituciones(): void {
    if (!this.formulario) return;

    if (!this.formulario.instituciones?.length) {
      const institucionBase = {
        nombreInstitucion: this.normalizarValorInstitucion(this.formulario.institucionPatrocinante),
        unidadInstitucional: this.normalizarValorInstitucion(this.formulario.unidadInstitucional),
        departamentoFacultad: this.normalizarValorInstitucion(this.formulario.departamentoFacultad),
        rol: 'principal' as const,
      };

      this.formulario.instituciones = [
        institucionBase.nombreInstitucion,
        institucionBase.unidadInstitucional,
        institucionBase.departamentoFacultad,
      ].every((valor) => valor.length > 0)
        ? [institucionBase]
        : [];
      this.institucionNueva.rol = this.formulario.instituciones.length === 0 ? 'principal' : 'asociada';
      return;
    }

    this.formulario.instituciones = this.formulario.instituciones
      .map((institucion, index): InstitucionFormulario => ({
        nombreInstitucion: this.normalizarValorInstitucion(institucion.nombreInstitucion),
        unidadInstitucional: this.normalizarValorInstitucion(institucion.unidadInstitucional),
        departamentoFacultad: this.normalizarValorInstitucion(institucion.departamentoFacultad),
        rol: index === 0 ? 'principal' : institucion.rol === 'principal' ? 'asociada' : institucion.rol,
      }))
      .filter((institucion) =>
        [institucion.nombreInstitucion, institucion.unidadInstitucional, institucion.departamentoFacultad].every((valor) => valor.length > 0)
      );
    this.institucionNueva.rol = this.formulario.instituciones.length === 0 ? 'principal' : this.institucionNueva.rol;
    this.sincronizarInstitucionPrincipal();
  }

  private sincronizarInstitucionPrincipal(): void {
    const principal = this.formulario.instituciones.find((institucion) => institucion.rol === 'principal') ?? this.formulario.instituciones[0];
    if (!principal) return;

    this.formulario.institucionPatrocinante = principal.nombreInstitucion;
    this.formulario.unidadInstitucional = principal.unidadInstitucional;
    this.formulario.departamentoFacultad = principal.departamentoFacultad;
  }

  private crearInstitucionVacia(rol: InstitucionFormulario['rol'] = 'asociada'): InstitucionFormulario {
    return {
      nombreInstitucion: '',
      unidadInstitucional: '',
      departamentoFacultad: '',
      rol,
    };
  }

  private normalizarValorInstitucion(valor: string | undefined): string {
    const limpio = String(valor ?? '').trim();
    return this.esValorInstitucionReal(limpio) ? limpio : '';
  }

  private esValorInstitucionReal(valor: string | undefined): boolean {
    const normalizado = String(valor ?? '').trim().toLowerCase();
    return normalizado.length > 0 && normalizado !== 'por definir';
  }
}
